<template>
  <div class="apikey-log" v-loading="loading">
    <!-- 页面标题 -->
    <WelcomeBanner title="API 调用日志" subtitle="查看与筛选该 Key 的调用记录">
      <template #right>
        <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
        <el-button type="primary" @click="refreshData" icon="el-icon-refresh">刷新</el-button>
      </template>
    </WelcomeBanner>

    <!-- API Key 信息 -->
    <BaseCard v-if="keyInfo" class="key-info-card">
      <template #header>
        <div class="card-header">
          <span class="header-title">
            <i class="el-icon-key"></i>
            API Key 信息
          </span>
          <el-tag :type="keyInfo.status === 1 ? 'success' : 'danger'" size="small" effect="dark">
            {{ keyInfo.status === 1 ? '有效' : '禁用' }}
          </el-tag>
        </div>
      </template>

      <div class="key-info">
        <div class="info-row">
          <div class="info-item">
            <span class="label">
              <i class="el-icon-document"></i> 备注：
            </span>
            <span class="value">{{ keyInfo.remark || '未命名' }}</span>
          </div>
          <div class="info-item">
            <span class="label">
              <i class="el-icon-finished"></i> Key ID：
            </span>
            <span class="value key-id">
              <code>{{ keyInfo.api_key_id }}</code>
              <el-button
                type="text"
                size="mini"
                icon="el-icon-document-copy"
                @click="copyText(keyInfo.api_key_id)"
                class="copy-btn"
              >复制</el-button>
            </span>
          </div>
        </div>
      </div>
    </BaseCard>

    <!-- 筛选区 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-date-picker
          v-model="dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          :picker-options="pickerOptions"
          @change="handleDateChange"
          style="width: 380px;"
        />
        <el-input
          v-model="filters.endpoint"
          placeholder="搜索接口路径"
          clearable
          style="width: 200px;"
          @clear="handleFilter"
          @keyup.enter.native="handleFilter"
          prefix-icon="el-icon-search"
        />
        <el-select v-model="filters.status" placeholder="响应状态" clearable style="width: 120px;">
          <el-option label="全部状态" value="" />
          <el-option label="成功 (2xx)" value="success" />
          <el-option label="失败 (4xx)" value="client_error" />
          <el-option label="错误 (5xx)" value="server_error" />
        </el-select>
        <el-button @click="handleReset" icon="el-icon-refresh-left">重置</el-button>
        <el-button type="primary" @click="handleFilter" icon="el-icon-search">搜索</el-button>
      </div>
      <!-- 快捷时间选择 -->
      <div class="quick-filters">
        <span class="quick-label">快捷筛选:</span>
        <el-button type="text" size="small" @click="setTimeRange('today')">今天</el-button>
        <el-button type="text" size="small" @click="setTimeRange('yesterday')">昨天</el-button>
        <el-button type="text" size="small" @click="setTimeRange('week')">近7天</el-button>
        <el-button type="text" size="small" @click="setTimeRange('month')">近30天</el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <StatCard :value="stats.successful" label="成功调用" :gradient="'var(--stat-gradient-2)'" :showPercent="true" :percent="stats.total>0 ? ((stats.successful/stats.total)*100).toFixed(1) : null" icon="el-icon-circle-check" />
      <StatCard :value="stats.failed" label="失败调用" :gradient="'var(--stat-gradient-3)'" :showPercent="true" :percent="stats.total>0 ? ((stats.failed/stats.total)*100).toFixed(1) : null" icon="el-icon-circle-close" />
      <StatCard :value="stats.total" label="总调用次数" :gradient="'var(--stat-gradient-4)'" icon="el-icon-document" />
      <StatCard :value="stats.avgDuration + ' ms'" label="平均耗时" :gradient="'var(--stat-gradient-5)'" icon="el-icon-time" />
    </div>

    <!-- 日志列表 -->
    <div class="log-container">
      <el-table :data="logList" stripe style="width: 100%" @row-click="showLogDetail">
        <el-table-column prop="log_id" label="日志ID" width="100" show-overflow-tooltip />
        <el-table-column prop="request_path" label="接口路径" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <code>{{ row.request_path }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="request_method" label="方法" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="getMethodType(row.request_method)" size="small">
              {{ row.request_method }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="response_code" label="状态码" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="getStatusType(row.response_code)" size="small">
              {{ row.response_code || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="client_ip" label="IP地址" width="140" />
        <el-table-column prop="response_time" label="响应时间" width="100" align="center">
          <template slot-scope="{ row }">
            <span :class="getDurationClass(row.response_time)">
              {{ row.response_time }}ms
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="调用时间" width="160">
          <template slot-scope="{ row }">
            {{ formatTime(row.create_time) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click.stop="showLogDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :current-page="pagination.currentPage"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 日志详情对话框 -->
    <el-dialog title="调用详情" :visible.sync="detailVisible" width="800px">
      <el-descriptions v-if="currentLog" :column="2" border>
        <el-descriptions-item label="日志ID">{{ currentLog.log_id }}</el-descriptions-item>
        <el-descriptions-item label="请求方法">
          <el-tag :type="getMethodType(currentLog.request_method)" size="small">
            {{ currentLog.request_method }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="接口路径" :span="2">
          <code>{{ currentLog.request_path }}</code>
        </el-descriptions-item>
        <el-descriptions-item label="状态码">
          <el-tag :type="getStatusType(currentLog.response_code)" size="small">
            {{ currentLog.response_code || '-' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="响应时间">{{ currentLog.response_time }}ms</el-descriptions-item>
        <el-descriptions-item label="IP地址">{{ currentLog.client_ip || '-' }}</el-descriptions-item>
        <el-descriptions-item label="调用时间" :span="2">{{ formatTime(currentLog.create_time) }}</el-descriptions-item>
      </el-descriptions>

      <!-- 查询参数 -->
      <div v-if="currentLog && currentLog.query_params" class="detail-section">
        <h4>查询参数</h4>
        <pre class="code-block">{{ formatJson(currentLog.query_params) }}</pre>
      </div>

      <!-- 请求体 -->
      <div v-if="currentLog && currentLog.body_params" class="detail-section">
        <h4>请求体</h4>
        <pre class="code-block">{{ formatJson(currentLog.body_params) }}</pre>
      </div>

      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getApiKeyLogs, getApiKeyList, getApiKeyStats } from '@/api/apiKey'
import BaseCard from '@/components/BaseCard.vue'
import StatCard from '@/components/StatCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'ApiKeyLog',
  components: { BaseCard, StatCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      keyId: null,
      keyInfo: null,
      logList: [],
      stats: {
        total: 0,
        successful: 0,
        failed: 0,
        avgDuration: 0
      },
      filters: {
        endpoint: '',
        status: ''
      },
      dateRange: [],
      pickerOptions: {
        shortcuts: [
          { text: '今天', onClick: (picker) => this.setPickerRange(picker, 'today') },
          { text: '昨天', onClick: (picker) => this.setPickerRange(picker, 'yesterday') },
          { text: '近7天', onClick: (picker) => this.setPickerRange(picker, 'week') },
          { text: '近30天', onClick: (picker) => this.setPickerRange(picker, 'month') }
        ]
      },
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      detailVisible: false,
      currentLog: null
    }
  },
  mounted() {
    this.keyId = this.$route.query.keyId
    if (this.keyId) {
      this.loadKeyInfo()
      this.loadLogs()
      this.loadStats()
    } else {
      this.$message.warning('未指定 API Key')
      this.goBack()
    }
  },
  methods: {
    async loadKeyInfo() {
      try {
        const data = await getApiKeyList({ page: 1, page_size: 100 })
        const list = data?.items || data?.list || data || []
        this.keyInfo = list.find(k => k.api_key_id === this.keyId)
      } catch (error) {
        console.error('加载 Key 信息失败:', error)
      }
    },

    async loadLogs() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          page_size: this.pagination.pageSize,
          endpoint: this.filters.endpoint || undefined,
          status: this.filters.status || undefined,
          start_time: this.dateRange?.[0] ? new Date(this.dateRange[0]).toISOString() : undefined,
          end_time: this.dateRange?.[1] ? new Date(this.dateRange[1]).toISOString() : undefined
        }
        const data = await getApiKeyLogs(this.keyId, params)
        this.logList = data?.items || data?.list || []
        this.pagination.total = data?.total || 0
      } catch (error) {
        console.error('加载日志失败:', error)
        this.$message.error('加载日志失败')
      } finally {
        this.loading = false
      }
    },

    async loadStats() {
      try {
        const data = await getApiKeyStats(this.keyId, {})
        if (data) {
          // 根据后端接口返回字段映射
          const total = data.total_calls || 0
          const valid = data.valid_calls || 0
          const invalid = data.invalid_calls || 0
          const totalTime = data.total_response_time_ms || 0
          // 计算平均响应时间
          const avgDuration = total > 0 ? Math.round(totalTime / total) : 0
          this.stats = {
            total: total,
            successful: valid,
            failed: invalid,
            avgDuration: avgDuration
          }
        }
      } catch (error) {
        console.error('加载统计失败:', error)
      }
    },

    refreshData() {
      this.loadLogs()
      this.loadStats()
    },

    handleFilter() {
      this.pagination.currentPage = 1
      this.loadLogs()
    },

    handleReset() {
      this.filters = { endpoint: '', status: '' }
      this.dateRange = []
      this.pagination.currentPage = 1
      this.loadLogs()
    },

    handleDateChange() {
      this.handleFilter()
    },

    setTimeRange(type) {
      const now = new Date()
      let start, end
      switch (type) {
        case 'today':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
          break
        case 'yesterday':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0)
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59)
          break
        case 'week':
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          end = now
          break
        case 'month':
          start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          end = now
          break
      }
      this.dateRange = [start, end]
      this.handleFilter()
    },

    setPickerRange(picker, type) {
      const now = new Date()
      let start, end
      switch (type) {
        case 'today':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
          break
        case 'yesterday':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0)
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59)
          break
        case 'week':
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          end = now
          break
        case 'month':
          start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          end = now
          break
      }
      picker.$emit('pick', [start, end])
    },

    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadLogs()
    },

    showLogDetail(row) {
      this.currentLog = row
      this.detailVisible = true
    },

    goBack() {
      this.$router.push({ name: 'ApiKeyManager' })
    },

    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },

    getMethodType(method) {
      const typeMap = {
        GET: 'success',
        POST: 'primary',
        PUT: 'warning',
        DELETE: 'danger',
        PATCH: 'info'
      }
      return typeMap[method?.toUpperCase()] || 'info'
    },

    getStatusType(status) {
      if (!status) return 'danger'
      if (status >= 200 && status < 300) return 'success'
      if (status >= 400 && status < 500) return 'warning'
      return 'danger'
    },

    getDurationClass(duration) {
      if (duration < 100) return 'duration-fast'
      if (duration < 500) return 'duration-normal'
      return 'duration-slow'
    },

    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },

    formatJson(str) {
      try {
        if (typeof str === 'string') {
          const obj = JSON.parse(str)
          return JSON.stringify(obj, null, 2)
        }
        return JSON.stringify(str, null, 2)
      } catch {
        return str || ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.apikey-log {
  padding: 20px;
  background-color: var(--bg-page);
  min-height: calc(100vh - 84px);

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
    border-radius: 10px;
    margin-bottom: 16px;
    box-shadow: 0 2px 12px rgba(129,140,248,0.22);

    h2 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #fff;
    }

    .header-actions {
      display: flex;
      gap: 8px;

      .el-button {
        height: 36px;
        background: rgba(255,255,255,0.12);
        color: #fff;
        border: none;
      }

      .el-button:hover { background: rgba(255,255,255,0.18); }
    }
  }

  .key-info-card {
    margin-bottom: 16px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);

    ::v-deep .el-card__header {
      padding: 12px 20px;
      border-bottom: 1px solid #f0f2f5;
      background: linear-gradient(135deg, rgba(129,140,248,0.04) 0%, rgba(167,139,250,0.02) 100%);
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-title {
        font-size: 15px;
        font-weight: 600;
        color: #324057;
        display: flex;
        align-items: center;
        gap: 8px;

        i { font-size: 16px; color: #6b7280; }
      }
    }

    .key-info { padding: 12px 0; }

    .info-row { display: flex; flex-wrap: wrap; gap: 32px; }

    .info-item { display: flex; align-items: center; }

    .label { color: #909399; margin-right: 8px; display:flex; align-items:center; gap:6px; }

    .value { color: #324057; font-weight: 500; }

    .value.key-id { display:flex; align-items:center; gap:8px; }

    .value.key-id code { font-family: 'Monaco','Menlo',monospace; font-size:13px; background:#f7f8fb; padding:6px 10px; border-radius:6px; }
    .copy-btn { padding:2px 6px; font-size:12px }
  }

  .filter-section {
    background: var(--bg-card);
    padding: 16px;
    border-radius: var(--radius-md);
    margin-bottom: 16px;
    box-shadow: var(--shadow-sm);

    .filter-row {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }
  }

  .stats-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }


  .quick-filters {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    gap: 8px;

    .quick-label {
      font-size: 13px;
      color: var(--text-muted);
    }
  }

  .log-container {
    background: #fff;
    padding: 16px;
    border-radius: 10px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);

    .pagination-wrapper { display:flex; justify-content:center; margin-top:16px }
  }
}

.detail-section {
  margin-top: 16px;

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: var(--text-primary);
  }

  .code-block {
    background: var(--bg-page);
    padding: 12px;
    border-radius: var(--radius-sm);
    font-size: 12px;
    font-family: 'Monaco', 'Menlo', monospace;
    max-height: 300px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
  }

  .error-block {
    background: #fef0f0;
    color: var(--danger-color);
    padding: 12px;
    border-radius: var(--radius-sm);
    font-size: 13px;
  }
}

.duration-fast {
  color: var(--success-color);
}

.duration-normal {
  color: #E6A23C;
}

.duration-slow {
  color: var(--danger-color);
}

code {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  background: var(--bg-page);
  padding: 2px 4px;
  border-radius: 2px;
}

@media (max-width: 1200px) {
  .apikey-log {
    .stats-cards {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (max-width: 768px) {
  .apikey-log {
    .stats-cards {
      grid-template-columns: 1fr;
    }

    .key-info-card .key-info {
      flex-direction: column;
      gap: 8px;
    }
  }
}
</style>
